import {
	ColorPicker,
	FormItem,
	InputWithMoreInfo,
	ValidationError
} from "client/Components";
import { ColorContrastRequirements } from "client/Components/ColorPicker/BaseColorPicker";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	calculateRelativeLuminance,
	cssColorToRgb,
	getContrastRatio,
	hexToRgb,
	parseComputedColor,
	RgbaColor,
	rgbaToHex,
	RgbColor
} from "client/Misc/colorFunctions";
import { isNotNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	MutableRefObject,
	ReactElement,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./ColorPickerWithPrimaryColorContrastRequirements.module.less";

export type ColorPickerWithPrimaryColorContrastRequirementsProps = {
	attachable: IValueInputAttachable<string | null>;
	label: string;
	onShowInfoDialog: () => void;
};

export const ColorPickerWithPrimaryColorContrastRequirements = (props: ColorPickerWithPrimaryColorContrastRequirementsProps): ReactElement => {
	const {
		attachable,
		label,
		onShowInfoDialog
	} = props;

	const [primaryAlternateHoverColor, setPrimaryAlternateHoverColor] = useState<string | null>(null);

	const primaryAlternateColorPlaceholderRef: MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const selectedColor: string | null = attachable
		.value;

	const primaryColorContrastRequirements = useMemo(
		(): ColorContrastRequirements => ({
			minimumRequiredContrastRatio: 4.5,
			compareAgainst: [
				"#ffffff",
				"#f8f8f8",
				...(primaryAlternateHoverColor
					? [primaryAlternateHoverColor]
					: []
				)
			],
			message: {
				kind: "warning",
				text: localize("_TheSelectedColorDoesntMeetAccessibilityStandardsWeRecommendInfo")
			}
		}),
		[primaryAlternateHoverColor]
	);

	const hasRequiredColorContrastRatio = useMemo(
		(): boolean => {
			if (!selectedColor) {
				return true;
			}

			const rgbColor: RgbColor | undefined = cssColorToRgb(selectedColor);

			if (!rgbColor) {
				return true;
			}

			return hasRequiredContrastRatio(
				rgbColor,
				primaryColorContrastRequirements
			);
		},
		[
			selectedColor,
			primaryColorContrastRequirements
		]
	);

	useLayoutEffect(
		(): void => {
			const { current: element } = primaryAlternateColorPlaceholderRef;

			if (!selectedColor
				|| !element
				|| !isValidHexColor(selectedColor)
			) {
				setPrimaryAlternateHoverColor(null);
				return;
			}

			const { backgroundColor } = window.getComputedStyle(element);
			const parsedColor: RgbaColor | undefined = parseComputedColor(backgroundColor);

			if (!parsedColor) {
				setPrimaryAlternateHoverColor(null);
				return;
			}

			setPrimaryAlternateHoverColor(
				rgbaToHex(parsedColor)
			);
		},
		[selectedColor]
	);

	return (
		<FormItem label={label}>
			<InputWithMoreInfo
				onMoreInfo={onShowInfoDialog}
			>
				<ColorPicker
					{...attachable.attachToColorPicker()}
					menuTitle={label}
					contrastRequirements={primaryColorContrastRequirements}
				/>
			</InputWithMoreInfo>
			<div
				className={styles.primaryAlternateColorPlaceholder}
				style={selectedColor
					? {
						"--_selected-color": selectedColor
					}
					: undefined
				}
				ref={primaryAlternateColorPlaceholderRef}
			/>
			{!hasRequiredColorContrastRatio && (
				<p className={styles.warning}>
					{primaryColorContrastRequirements.message?.text}
				</p>
			)}
			<ValidationError {...attachable.attachToValidationError()} />
		</FormItem>
	);
};
ColorPickerWithPrimaryColorContrastRequirements.displayName = "ColorPickerWithPrimaryColorContrastRequirements";

function isValidHexColor(color: string): boolean {
	return isNotNil(hexToRgb(color));
}

export function hasRequiredContrastRatio(
	rgbColor: RgbColor,
	contrastRequirements: ColorContrastRequirements
): boolean {
	const {
		compareAgainst,
		minimumRequiredContrastRatio
	} = contrastRequirements;

	const compareAgainstLuminances = compareAgainst
		.map(cssColorToRgb)
		.filter(isNotNil)
		.map(calculateRelativeLuminance);

	return compareAgainstLuminances
		.map(l => getContrastRatio(rgbColor, l))
		.every(c => c >= minimumRequiredContrastRatio);
}